import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@apollo/client";
import GetUserDemographics from "../queries/GetUserDemographics";
import Select from "react-select";
import Spinner from "./Spinner";
import writeXlsxFile from "write-excel-file";
import { useAppContext } from "../AppContext";
// reactstrap components
import {
  Card,
  CardHeader,
  CardBody,
  Table,
  Row,
  Col,
  FormGroup,
  Button,
} from "reactstrap";

const Demographics = () => {
  const { error, loading, data, refetch } = useQuery(GetUserDemographics, {
    // fetchPolicy: "network-only",
    variables: {
      survey_language: null,
      trial_type: null,
      survey_type: null,
    },
  });
  const [selectedLanguage, setSelectedLanguage] = useState({
    value: null,
    label: "None",
  });
  const [selectedTrialType, setSelectedTrialType] = useState({
    value: null,
    label: "None",
  });
  const [selectedSurveyType, setSelectedSurveyType] = useState({
    value: null,
    label: "None",
  });
  const users = useRef([]);
  const { displayToast, closeToast } = useAppContext();
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const runRefetch = async () => {
      let variables;

      if (
        (!selectedLanguage || selectedLanguage.value === null) &&
        (!selectedTrialType || selectedTrialType.value === null) &&
        (!selectedSurveyType || selectedSurveyType.value === null)
      ) {
        if (users.current.length === 0) return;

        variables = {
          survey_language: null,
          trial_type: null,
          survey_type: null,
        };
      } else {
        variables = {
          survey_language: selectedLanguage?.value ?? null,
          trial_type: selectedTrialType?.value ?? null,
          survey_type: selectedSurveyType?.value ?? null,
        };
      }

      setIsFetching(true);

      try {
        await refetch(variables);
      } finally {
        setIsFetching(false);
      }
    };

    runRefetch();
  }, [selectedLanguage, selectedTrialType, selectedSurveyType, refetch]);

  if (loading) return <Spinner />;
  if (error) {
    console.log("error:");
    console.log(error);
    return <p>Something Went Wrong...</p>;
  }

  // Assuming `data.users` is an array of user objects
  users.current = data.users.filter(
    (user) =>
      user.surveys && Array.isArray(user.surveys) && user.surveys.length > 0,
  );

  console.log("users: ", users.current);

  function generateExcel() {
    let success = users.current !== null && users.current.length !== 0;

    const toast = {
      title: success ? "Success" : "Error",
      color: success ? "bg-success" : "bg-danger",
      icon: success ? "check-bold" : "fat-remove",
      message: success ? "XML is being generated" : "No data to place in XML",
    };

    displayToast(toast);

    setTimeout(() => {
      closeToast();
    }, 5000);

    if (!success) return;

    console.log("users: ", users.current);

    let tableData = [];

    tableData.push([
      {
        type: String,
        value: "User ID",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "TL / SME",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Length of Time",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Current / Former FAO",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Rank",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Length of Time",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Overseas Assignment",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
      {
        type: String,
        value: "Language Cert / Level",
        align: "center",
        width: 12,
        backgroundColor: "#a8aabd",
      },
    ]);

    users.current.forEach((user, uIndex) => {
      let certString = "";

      if (user.ilr) {
        certString = certString + "ILR:";

        if (user.ilr_listening)
          certString = certString + " L-" + user.ilr_listening;
        if (user.ilr_reading)
          certString = certString + " R-" + user.ilr_reading;

        certString = certString + "||";
      }

      if (user.dlpt) {
        certString = certString + "DLPT:";

        if (user.dlpt_listening)
          certString = certString + " L-" + user.dlpt_listening;
        if (user.dlpt_reading)
          certString = certString + " R-" + user.dlpt_reading;

        certString = certString + "||";
      }

      if (user.opi) {
        certString = certString + "OPI:";

        if (user.opi_speaking)
          certString = certString + " S-" + user.opi_speaking;

        certString = certString + "||";
      }

      if (user.other) {
        certString = certString + user.other_string + ":";

        if (user.other_listening)
          certString = certString + " L-" + user.other_listening;
        if (user.other_reading)
          certString = certString + " R-" + user.other_reading;
        if (user.other_speaking)
          certString = certString + " S-" + user.other_speaking;
      }

      if (certString === "") {
        certString = "No language certifications reported";
      }

      tableData.push([
        {
          type: String,
          value: user.user_name,
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_tl_or_sme ? "Yes" : "No",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_tl_or_sme ? user.tl_sme_lot : "Not Applicable",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_former_fao ? "Yes" : "No",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_former_fao ? user.fao_rank : "N/A",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_former_fao ? user.fao_lot : "N/A",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: user.current_former_fao
            ? user.fao_assignment
              ? "Yes"
              : "No"
            : "N/A",
          align: "center",
          width: 12,
        },
        {
          type: String,
          value: certString,
          align: "center",
          width: 12,
        },
      ]);
    });

    // Get the current date
    const currentDate = new Date();

    // Format the date as YYYY-MM-DD
    const formattedDate = currentDate.toISOString().split("T")[0]; // This will give you a string like "2023-04-01"

    // Create the filename by including the formatted date
    const fileName = `demographics_${formattedDate}.xlsx`;

    // Use the fileName in your writeXlsxFile function
    writeXlsxFile(tableData, {
      fileName: fileName,
    });
  }

  function displayLanguageCerts(user) {
    let dataToDisplay = [];

    if (user.ilr) {
      let thisData = "ILR:";

      if (user.ilr_listening) thisData = thisData + " L-" + user.ilr_listening;
      if (user.ilr_reading) thisData = thisData + " R-" + user.ilr_reading;

      dataToDisplay.push(thisData);
    }

    if (user.dlpt) {
      let thisData = "DLPT:";

      if (user.dlpt_listening)
        thisData = thisData + " L-" + user.dlpt_listening;
      if (user.dlpt_reading) thisData = thisData + " R-" + user.dlpt_reading;

      dataToDisplay.push(thisData);
    }

    if (user.opi) {
      let thisData = "OPI:";

      if (user.opi_speaking) thisData = thisData + " S-" + user.opi_speaking;

      dataToDisplay.push(thisData);
    }

    if (user.other) {
      let thisData = user.other_string + ":";

      if (user.other_listening)
        thisData = thisData + " L-" + user.other_listening;
      if (user.other_reading) thisData = thisData + " R-" + user.other_reading;
      if (user.other_speaking)
        thisData = thisData + " S-" + user.other_speaking;

      dataToDisplay.push(thisData);
    }

    if (dataToDisplay.length === 0) {
      return "No language certifications reported";
    } else {
      return dataToDisplay.map((data, dIndex) => {
        return <div key={`${user.user_name}-languages-${dIndex}`}>{data}</div>;
      });
    }
  }

  function displayUsers() {
    return users.current.map((user, uIndex) => {
      if (user.role === "admin") return null;

      return (
        <tr key={user.user_name}>
          <td>{user.user_name}</td>
          <td>{user.current_tl_or_sme ? "Yes" : "No"}</td>
          <td>{user.current_tl_or_sme ? user.tl_sme_lot : "Not Applicable"}</td>
          <td>{user.current_former_fao ? "Yes" : "No"}</td>
          <td>{user.current_former_fao ? user.fao_rank : "N/A"}</td>
          <td>{user.current_former_fao ? user.fao_lot : "N/A"}</td>
          <td>
            {user.current_former_fao
              ? user.fao_assignment
                ? "Yes"
                : "No"
              : "N/A"}
          </td>
          <td
            style={{
              whiteSpace: "normal",
              wordWrap: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {displayLanguageCerts(user)}
          </td>
        </tr>
      );
    });
  }

  return (
    <>
      <Button color="primary" onClick={() => generateExcel()}>
        Create Excel
      </Button>
      <Card className="bg-secondary shadow">
        <CardHeader className="bg-white border-0">
          <Row className="align-items-center">
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-language">
                  Language
                </label>
                <Select
                  value={selectedLanguage}
                  onChange={(selectedLanguage) => {
                    setSelectedLanguage(selectedLanguage);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "arabic",
                      label: "Arabic",
                    },
                    {
                      value: "korean",
                      label: "Korean",
                    },
                    {
                      value: "russian",
                      label: "Russian",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-language"
                />
              </FormGroup>
            </Col>
            <Col sm="6" lg="4">
              <FormGroup>
                <label className="form-control-label" htmlFor="input-trial">
                  Trial Type
                </label>
                <Select
                  value={selectedTrialType}
                  onChange={(selectedTrialType) => {
                    setSelectedTrialType(selectedTrialType);
                  }}
                  options={[
                    {
                      value: null,
                      label: "None",
                    },
                    {
                      value: "individual",
                      label: "Individual",
                    },
                    {
                      value: "group",
                      label: "Group",
                    },
                  ]}
                  className="form-control-alternative"
                  id="input-trial"
                />
              </FormGroup>
            </Col>
          </Row>
        </CardHeader>
        <CardBody className="p-0">
          {users.current !== null && users.current.length > 0 ? (
            <>
              <div className="d-flex justify-content-center py-2 w-100 font-weight-bold table-dark">
                Course II
              </div>
              <Table>
                <thead>
                  <th>USER ID</th>
                  <th>TL / SME</th>
                  <th>LENGTH OF TIME</th>
                  <th>CURRENT / FORMER FAO</th>
                  <th>RANK</th>
                  <th>LENGTH OF TIME</th>
                  <th>OVERSEAS ASSIGNMENT</th>
                  <th>LANGUAGE CERT/LEVEL</th>
                </thead>
                <tbody>{displayUsers()}</tbody>
              </Table>
            </>
          ) : isFetching ? (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <Spinner />
              <h3 className="text-muted mt-3">Loading results...</h3>
            </div>
          ) : (
            <div className="d-flex flex-column p-5 my-5 h-100 w-100 justify-content-center align-items-center">
              <h1>
                <i className="ni ni-bell-55 text-muted" />
              </h1>
              <h3 className="text-muted">
                Please select what you'd like to view
              </h3>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default Demographics;
